/**
 * Toolbar — Draggable FAB that expands to show controls
 * Collapsed: small circle with "LR" logo
 * Expanded: horizontal bar with language selector, progress, exit
 */

export type OnLanguageChange = (locale: string) => void;
export type OnExit = () => void;
export type OnCaptureSnapshot = () => void;
/** Fired when the user toggles Browse mode. `browsing=true` means the
 *  inspector should be suspended so page links navigate natively. */
export type OnBrowseToggle = (browsing: boolean) => void;

export interface ToolbarCaptureOptions {
  /** 'capture' = new snapshot, 'recapture' = overwrite an existing snapshot's version */
  mode?: 'capture' | 'recapture';
}

export class Toolbar {
  private el: HTMLDivElement;
  private langBtnEl: HTMLButtonElement;
  private langMenuEl: HTMLDivElement;
  private progressEl: HTMLSpanElement;
  private expanded = false;
  private currentLocale: string;
  private onLanguageChange: OnLanguageChange;
  private onExit: OnExit;
  private onCaptureSnapshot: OnCaptureSnapshot | null;
  private onBrowseToggle: OnBrowseToggle | null;
  private browsing = false;
  private browseBtnEl: HTMLButtonElement | null = null;

  // Drag state
  private dragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  constructor(
    shadowRoot: ShadowRoot,
    languages: string[],
    currentLocale: string,
    onLanguageChange: OnLanguageChange,
    onExit: OnExit,
    onCaptureSnapshot: OnCaptureSnapshot | null = null,
    captureOptions: ToolbarCaptureOptions = {},
    onBrowseToggle: OnBrowseToggle | null = null,
  ) {
    this.onLanguageChange = onLanguageChange;
    this.onExit = onExit;
    this.onCaptureSnapshot = onCaptureSnapshot;
    this.onBrowseToggle = onBrowseToggle;
    this.currentLocale = currentLocale;
    const captureMode = captureOptions.mode ?? 'capture';

    this.el = document.createElement('div');
    this.el.className = 'lr-fab';

    // FAB button (always visible)
    const fabBtn = document.createElement('button');
    fabBtn.className = 'lr-fab-btn';
    fabBtn.textContent = 'LR';
    fabBtn.addEventListener('click', (e) => {
      // Don't toggle if we just finished dragging
      if (this.dragging) return;
      e.stopPropagation();
      this.toggle();
    });
    // Note: fabBtn is appended to this.el at the END (after panel), so flex
    // lays out panel-then-button — i.e. the panel expands to the LEFT of the
    // button. The button's default position is near the right edge of the
    // viewport, so leftward expansion keeps the panel on-screen.

    // Expanded panel
    const panel = document.createElement('div');
    panel.className = 'lr-fab-panel';

    // Custom language dropdown
    const langWrap = document.createElement('div');
    langWrap.className = 'lr-lang-dropdown';

    this.langBtnEl = document.createElement('button');
    this.langBtnEl.className = 'lr-lang-btn';
    this.langBtnEl.textContent = currentLocale.toUpperCase();
    this.langBtnEl.addEventListener('click', (e) => {
      e.stopPropagation();
      this.langMenuEl.classList.toggle('open');
    });
    langWrap.appendChild(this.langBtnEl);

    this.langMenuEl = document.createElement('div');
    this.langMenuEl.className = 'lr-lang-menu';
    for (const lang of languages) {
      const item = document.createElement('button');
      item.className = 'lr-lang-item';
      if (lang === currentLocale) item.classList.add('active');
      item.textContent = lang.toUpperCase();
      item.addEventListener('click', (e) => {
        e.stopPropagation();
        this.currentLocale = lang;
        this.langBtnEl.textContent = lang.toUpperCase();
        this.langMenuEl.classList.remove('open');
        // Update active state
        this.langMenuEl.querySelectorAll('.lr-lang-item').forEach((el) => el.classList.remove('active'));
        item.classList.add('active');
        this.onLanguageChange(lang);
      });
      this.langMenuEl.appendChild(item);
    }
    langWrap.appendChild(this.langMenuEl);
    panel.appendChild(langWrap);

    // Capture Snapshot button (only in capture mode, not viewer mode).
    // Label/title change when recapture mode is active so the PM knows this
    // click will overwrite an existing snapshot's version instead of making
    // a new one.
    if (this.onCaptureSnapshot) {
      const captureBtn = document.createElement('button');
      captureBtn.className = 'lr-fab-snapshot';
      if (captureMode === 'recapture') {
        captureBtn.textContent = 'Save re-capture';
        captureBtn.title = 'Save this page as a new version of the existing snapshot';
      } else {
        captureBtn.textContent = 'Capture';
        captureBtn.title = 'Capture a redacted snapshot of this page for translators';
      }
      captureBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.onCaptureSnapshot) this.onCaptureSnapshot();
      });
      panel.appendChild(captureBtn);
    }

    // Browse-mode toggle — suspends the inspector so the PM can click the
    // site's own nav links to reach another page without every click opening
    // the edit drawer. Only shown when a handler is wired (live-page editing,
    // not snapshot viewer mode).
    if (this.onBrowseToggle) {
      const browseBtn = document.createElement('button');
      browseBtn.className = 'lr-fab-browse';
      browseBtn.textContent = 'Browse';
      browseBtn.title = 'Pause editing so you can click links and navigate to another page';
      browseBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.setBrowsing(!this.browsing);
      });
      this.browseBtnEl = browseBtn;
      panel.appendChild(browseBtn);
    }

    // Progress
    this.progressEl = document.createElement('span');
    this.progressEl.className = 'lr-fab-progress';
    panel.appendChild(this.progressEl);

    // Exit button
    const exitBtn = document.createElement('button');
    exitBtn.className = 'lr-fab-exit';
    exitBtn.textContent = 'Exit';
    exitBtn.addEventListener('click', () => this.onExit());
    panel.appendChild(exitBtn);

    this.el.appendChild(panel);
    this.el.appendChild(fabBtn);
    shadowRoot.appendChild(this.el);

    // Dragging
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    fabBtn.addEventListener('mousedown', this.handleMouseDown);
  }

  toggle(): void {
    this.expanded = !this.expanded;
    this.el.classList.toggle('expanded', this.expanded);
  }

  private setBrowsing(browsing: boolean): void {
    this.browsing = browsing;
    if (this.browseBtnEl) {
      this.browseBtnEl.textContent = browsing ? 'Editing' : 'Browse';
      this.browseBtnEl.classList.toggle('active', browsing);
      this.browseBtnEl.title = browsing
        ? 'Resume editing — clicks open the edit drawer again'
        : 'Pause editing so you can click links and navigate to another page';
    }
    this.el.classList.toggle('lr-fab--browsing', browsing);
    if (this.onBrowseToggle) this.onBrowseToggle(browsing);
  }

  updateProgress(translated: number, total: number): void {
    this.progressEl.textContent = `${translated}/${total}`;
  }

  getCurrentLocale(): string {
    return this.currentLocale;
  }

  destroy(): void {
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
    this.el.remove();
  }

  private handleMouseDown(e: MouseEvent): void {
    this.dragging = false;
    this.dragOffsetX = e.clientX - this.el.getBoundingClientRect().left;
    this.dragOffsetY = e.clientY - this.el.getBoundingClientRect().top;
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  }

  private handleMouseMove(e: MouseEvent): void {
    this.dragging = true;
    const x = Math.max(0, Math.min(e.clientX - this.dragOffsetX, window.innerWidth - this.el.offsetWidth));
    const y = Math.max(0, Math.min(e.clientY - this.dragOffsetY, window.innerHeight - this.el.offsetHeight));

    // Anchor by `right` (not `left`) so the FAB grows the panel LEFTWARD
    // when expanded, keeping the button visually fixed where the user
    // dropped it. With `left`-anchoring the container's left edge stays
    // pinned and the panel pushes the button rightward on expand — that's
    // the "FAB jumps off-screen after drag" bug. Switching to `right`
    // here means the button's right edge stays put across the drag /
    // expand / language-switch cycle.
    const rightOffset = window.innerWidth - x - this.el.offsetWidth;
    this.el.style.right = `${Math.max(0, rightOffset)}px`;
    this.el.style.top = `${y}px`;
    this.el.style.left = 'auto';
    this.el.style.bottom = 'auto';
  }

  private handleMouseUp(): void {
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
    // Reset dragging flag after a tick so the click handler can check it
    if (this.dragging) {
      setTimeout(() => { this.dragging = false; }, 0);
    }
  }
}
